import { get } from 'helpers/api';
import { parsePlaceDetails } from 'components/form/autocompleted_address_form_group/helpers';

const DEFAULT_SEARCH_PARAMS = {
  'polygon_geojson': '1',
  'polygon_threshold': '0.001',
  'format': 'json',
  'limit': '1'
};

export function boundsFromMultiplePolygons(polygons, bounds = new google.maps.LatLngBounds()) {
  polygons.forEach((polygon) => {
    bounds.union(boundsFromPolygon(polygon));
  });

  return bounds;
}

export function boundsFromPolygon(coords, bounds = new google.maps.LatLngBounds()) {
  coords.forEach(coord => bounds.extend(coord));

  return bounds;
}

export function boundsFromGeoJson(geoJson, bounds = new google.maps.LatLngBounds()) {
  if (geoJson.type == 'Polygon') {
    geoJson.coordinates.forEach(coords => coords.forEach(([lng, lat]) => bounds.extend({ lat, lng })));
  } else if (geoJson.type == 'MultiPolygon') {
    geoJson.coordinates.forEach((shape) => {
      shape.forEach(coords => coords.forEach(([lng, lat]) => bounds.extend({ lat, lng })));
    });
  }

  return bounds;
}

export function nominatimGetUrl(url, params) {
  const searchParams = new URLSearchParams({
    ...DEFAULT_SEARCH_PARAMS,
    ...params
  }).toString();

  return `${url}search?${searchParams}`;
}

function nominatimQueryParams(details) {
  if (details.addressComponents) {
    const placeDetails = parsePlaceDetails(details.addressComponents);

    if (!placeDetails.address) {
      return placeDetails;
    }
  }

  if (details.types.includes('administrative_area_level_1')) {
    // State level area
    return { state: details.address.replace(', USA', '') };
  } else {
    return { q: details.address };
  }
}

export function fetchGeoJSONFromNominatimAsync(
  url: string,
  detail: { address: string }
): Promise<{ geoJson: any, address: string} | null> {
  return new Promise((resolve) => {
    fetchGeoJSONFromNominatim(url, detail, {
      successCallback: geoJson => resolve({ geoJson, address: detail.address }),
      noResultCallback: () => resolve(null),
    });
  });
}

export function fetchGeoJSONFromNominatim(url, details, options) {
  get(nominatimGetUrl(url, nominatimQueryParams(details))).then((data) => {
    if (data && data.length > 0 && data[0].geojson && ['Polygon', 'MultiPolygon'].includes(data[0].geojson.type)) {
      return options.successCallback(data[0].geojson);
    }

    options.noResultCallback();
  });
}

export function placeBounds(place) {
  if (place.viewport) {
    return polygonFromBounds(place.viewport);
  } else {
    const circle = new google.maps.Circle({
      center: place.location,
      radius: 8046.72 // 5 miles
    });

    return polygonFromBounds(circle.getBounds());
  }
}

export const polygonFromBounds = (bounds) => {
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();

  return [
    { lat: ne.lat(), lng: ne.lng() },
    { lat: sw.lat(), lng: ne.lng() },
    { lat: sw.lat(), lng: sw.lng() },
    { lat: ne.lat(), lng: sw.lng() }
  ];
};

export function getDetailsOfPlace(
  query: google.maps.places.PlaceDetailsRequest,
  placesService = new google.maps.places.PlacesService(document.createElement('div'))
): Promise<google.maps.places.PlaceResult> {
  return new Promise((resolve, reject) => {
    if (!query.placeId) reject('Property ID required for details');
    placesService.getDetails(query, (place, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && place) {
        resolve(place);
      } else {
        reject(status);
      }
    });
  });
}

// fullAddress is place name from google autocomplete, usually in the format of
// ADDRESS, CITY, STATE, COUNTRY
export function splitFullAddress(fullAddress) {
  const addressArr = fullAddress.split(',').map(text => text.trim());
  // STATE could be a (ST ZIP) format or in special cases the city proper
  if (addressArr[2].length > 2) {
    addressArr[2] = (addressArr[3] ? addressArr[3] : addressArr[2]).substring(0, 2);
  }
  return addressArr;
}
